import { useCallback } from "react";
import { useAuth } from "react-oidc-context";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
const DEFAULT_TIMEOUT = 600000;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

export function useApi() {
  const auth = useAuth();

  const getAuthHeaders = useCallback(() => {
    if (!auth.isAuthenticated) {
      throw new Error("Not authenticated");
    }

    return {
      Authorization: `Bearer ${auth.user?.access_token}`,
    };
  }, [auth.isAuthenticated, auth.user?.access_token]);

  const fetchWithTimeout = useCallback(async (url, options = {}, timeout = DEFAULT_TIMEOUT) => {
    let lastError;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        lastError = error;

        if (error.name === "AbortError" || attempt === MAX_RETRIES) {
          throw error;
        }

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * (attempt + 1)));
      }
    }

    throw lastError;
  }, []);

  const fetchGet = useCallback(async (endpoint, options = {}) => {
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    return fetchWithTimeout(
      `${API_BASE_URL}${endpoint}`,
      {
        method: "GET",
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
        ...options,
      },
      timeout
    );
  }, [fetchWithTimeout, getAuthHeaders]);

  const fetchPost = useCallback(async (endpoint, body, options = {}) => {
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    return fetchWithTimeout(
      `${API_BASE_URL}${endpoint}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
          ...options.headers,
        },
        body: JSON.stringify(body),
      },
      timeout
    );
  }, [fetchWithTimeout, getAuthHeaders]);

  return {
    fetchGet,
    fetchPost,
    isAuthenticated: auth.isAuthenticated,
    apiBaseUrl: API_BASE_URL,
    // Cognito ID token — carries the email claim the access token lacks.
    // Audit-relevant POSTs send it as X-Id-Token; the backend trusts it only
    // after signature verification + a sub match against the Bearer token.
    idToken: auth.user?.id_token,
  };
}

export default useApi;
